import { useEffect } from 'react'
import { useTimelineEditingLease } from '../../hooks/useTimelineEditingLease'
import { refreshMatterAfterWriteAccessError } from '../../utils/refresh-matter-after-write-access-error'
import { useAppDispatch } from '@/store/store-hooks'
import { isoDateStringToMonthDayYear } from '@/util/time'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackTimelineEvent } from '@/analytics/utils/timeline-analytics'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import MatterRecordRemoveFileDialog from '@/matter-asset-common/files/components/MatterRecordRemoveFileDialog'
import { MatterActivityFeedActions } from '@/matter/activity-feed/store/slice'
import { getRemovalImpact } from '../../fetch/get-removal-impact'
import { deleteAssetFile } from '../../fetch/delete-asset-file'
import { TimelineAssetActions } from '../../store/slice'
import { RemovalImpactResponse } from '../../store/schemas'

type RemoveFileDialogProps = {
  open: boolean
  assetId: string
  matterId: string
  userFileId: string
  displayName: string
  onClose: () => void
}

/**
 * Remove-file confirmation for the timeline. Thin wrapper over the shared RemoveFileDialog
 * that supplies the timeline copy, impact fetch and sentence, and the removal itself.
 */
export default function RemoveFileDialog({ open, assetId, matterId, userFileId, displayName, onClose }: RemoveFileDialogProps) {
  const dispatch = useAppDispatch()
  const lease = useTimelineEditingLease()
  useEffect(() => {
    if (!lease.canWrite) onClose()
  }, [lease.canWrite, onClose])

  const describeImpact = (impact: RemovalImpactResponse) => {
    const eventsPhrase = `${impact.events_count} event${impact.events_count === 1 ? '' : 's'}`
    if (impact.earliest_event_date && impact.latest_event_date) {
      return `Removing this file will delete ${eventsPhrase} from ${isoDateStringToMonthDayYear(impact.earliest_event_date)} – ${isoDateStringToMonthDayYear(
        impact.latest_event_date
      )} and regenerate the timeline.`
    }
    return `Removing this file will delete ${eventsPhrase} and regenerate the timeline.`
  }

  const handleConfirm = async () => {
    if (!lease.canWrite) {
      onClose()
      return
    }
    let result
    try {
      result = await deleteAssetFile(assetId, userFileId)
    } catch (error) {
      if (lease.handleError(error)) {
        onClose()
        return
      }
      refreshMatterAfterWriteAccessError(error, matterId, dispatch)
      throw error
    }
    trackTimelineEvent(AnalyticsEvent.TimelineAssetFileRemoved, { timelineAssetId: assetId, timelineEventsRemoved: result.events_removed })
    // Server truth: refresh the overview, events, and roster after the regeneration
    dispatch(TimelineAssetActions.fetchAssetForMatter({ matterId }))
    dispatch(TimelineAssetActions.refreshAssetData({ assetId }))
    // The removal is a completed change to the record, which moves its row in the matter's activity.
    dispatch(MatterActivityFeedActions.invalidateMatterActivity({ matterId }))
    // Removal is immediate (no regeneration wait), so confirm it with a toast.
    dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Removed ${displayName} from the timeline`, durationMs: kToastDurationShortMs }))
  }

  return (
    <MatterRecordRemoveFileDialog
      open={open}
      displayName={displayName}
      onClose={onClose}
      title="Remove file from timeline"
      confirmLabel="Remove and regenerate"
      impactKey={`${assetId}:${userFileId}`}
      fetchImpact={() =>
        getRemovalImpact(assetId, userFileId).catch((error) => {
          refreshMatterAfterWriteAccessError(error, matterId, dispatch)
          throw error
        })
      }
      describeImpact={describeImpact}
      impactUnavailableMessage="The impact summary could not be loaded. Removing this file will delete its events and regenerate the timeline."
      conflictMessage="The timeline is being updated. Try again when the current update finishes."
      onConfirm={handleConfirm}
    />
  )
}
